/**
 * Monte Carlo simulation (spec §14). Pure logic — the Web Worker wraps this,
 * and tests call it directly with a fixed seed.
 *
 * Two return models:
 *  - 'bootstrap' (default): contiguous 8-year blocks sampled from the bundled
 *    1928-2023 historical series. Blocks, not independent draws, because
 *    independent draws destroy the serial correlation that makes
 *    sequence-of-returns risk behave as it actually does.
 *  - 'parametric': lognormal draws from the active profile's means with
 *    configurable volatilities and equity/bond correlation.
 *
 * IMPORTANT — the two models must be fed the same expectation, or they answer
 * different questions and appear to contradict each other. Raw history averaged
 * ~11.6% on equities, well above the ~7% a typical plan assumes, so an
 * unadjusted bootstrap is systematically rosier than the parametric run on the
 * same plan. By default the bootstrap is therefore RECENTERED: every sampled
 * year is shifted by a constant so the series mean equals the active profile's
 * expected return, which preserves history's volatility, its crash-and-recovery
 * ordering and its fat tails while removing the disagreement about the average.
 * `recenter: false` restores the raw historical series for anyone who
 * deliberately wants to bet on history repeating its returns as well as its
 * shape.
 *
 * Every trial runs the FULL projection engine — taxes, RMDs, Social Security,
 * ACA, the works — with that trial's return sequence.
 */
import type { Plan } from '../model/types';
import { runProjection, type YearReturns } from './projection';
import { activeAssumptions } from './projection';
import { CURRENT_YEAR } from '../model/defaults';
import {
  EQUITY_RETURNS, BOND_RETURNS, CASH_RETURNS, HISTORICAL_YEARS,
} from './data/historicalReturns';

// ── Seeded RNG (mulberry32): deterministic, reproducible in tests ───────────
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s |= 0; s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Standard normal via Box-Muller. */
function gaussian(rng: () => number): number {
  const u = Math.max(rng(), 1e-12);
  const v = rng();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

const mean = (xs: readonly number[]) => xs.reduce((t, x) => t + x, 0) / xs.length;

/**
 * Compound (geometric) annual return of a series — what a dollar actually grew
 * at. Always below the arithmetic mean, and the gap widens with volatility:
 * that difference is why a simulated median trails a straight-line projection
 * drawn at the same "average" return.
 */
const geometricMean = (xs: readonly number[]): number =>
  Math.exp(xs.reduce((t, x) => t + Math.log(1 + x), 0) / xs.length) - 1;

/**
 * Both readings of the bundled history. The planner quotes COMPOUND returns
 * everywhere (that is what people mean by "I assume 7% a year"), so
 * HISTORICAL_MEANS holds the compound figures and the arithmetic ones are kept
 * alongside for disclosure.
 */
export const HISTORICAL_MEANS = {
  equity: geometricMean(EQUITY_RETURNS),
  bond: geometricMean(BOND_RETURNS),
  cash: geometricMean(CASH_RETURNS),
} as const;

export const HISTORICAL_ARITHMETIC_MEANS = {
  equity: mean(EQUITY_RETURNS),
  bond: mean(BOND_RETURNS),
  cash: mean(CASH_RETURNS),
} as const;

export interface MonteCarloOptions {
  trials?: number; // default 1,000
  model?: 'bootstrap' | 'parametric';
  blockLength?: number; // bootstrap block size, default 8
  /**
   * Bootstrap only. True (the default) shifts the historical series onto the
   * active profile's expected returns so both models share one expectation;
   * false replays history's own averages.
   */
  recenter?: boolean;
  /** Cut discretionary spending by this fraction after down years (0 = rigid). */
  flexibleSpendingCut?: number;
  seed?: number;
  /** Parametric volatilities and correlation. */
  equitySd?: number;
  bondSd?: number;
  equityBondCorrelation?: number;
  /** Called every ~25 trials so the UI can show progress. */
  onProgress?: (done: number, total: number) => void;
}

export interface MonteCarloResult {
  trials: number;
  model: 'bootstrap' | 'parametric';
  /** Whether the historical series was recentered onto the profile's returns. */
  recentered: boolean;
  /** The expected stock return this run actually used — what the models must
   *  agree on for their answers to be comparable. */
  equityMeanUsed: number;
  successRate: number;
  /** Per plan year: percentile bands of net worth in TODAY'S dollars. */
  bands: { year: number; p10: number; p25: number; p50: number; p75: number; p90: number }[];
  /**
   * Cumulative inflation for each entry in `bands`, so a caller can show the
   * same results in future (nominal) dollars by multiplying. Exact rather than
   * an approximation: inflation is not stochastic here, so every trial shares
   * this one path.
   */
  inflationFactors: number[];
  /** Depletion year of each failed trial (empty when all succeed). */
  failureYears: number[];
  /** Terminal net worth (today's dollars) for every trial, sorted ascending. */
  terminalWealths: number[];
  /** Flexible-spending: average and worst count of cut years per trial. */
  avgCutYears: number;
  worstCutYears: number;

  // ── Reconciliation with the straight-line projection on screen ──
  /** Ending net worth (today's dollars) of the ordinary deterministic run,
   *  computed with the same spending rule so only returns differ. */
  deterministicTerminal: number;
  /** Where that straight-line result falls among these trials, 0-100. Well
   *  above 50 means the headline numbers describe a luckier-than-typical run. */
  deterministicPercentile: number;
  /** Among trials that ran dry: median annual spending gap once short, and the
   *  median age at which the shortfall began. Magnitude, not just a count. */
  medianShortfallPerYear: number;
  medianShortfallAge: number | null;
}

/**
 * Build one trial's return sequence via contiguous historical blocks.
 *
 * `recenterTo` scales every drawn year so the series COMPOUNDS at those target
 * returns. Growth factors are scaled by a constant (1+r' = (1+r)·k) rather than
 * shifted, which keeps the compounding arithmetic exact, preserves the spread
 * of log returns, and can never push a year below -100%. History's shape is
 * untouched: 1931 stays the worst year and 2008 still follows 2007. Only the
 * long-run growth rate moves.
 */
export function bootstrapSequence(
  years: number,
  blockLength: number,
  rng: () => number,
  recenterTo?: { equity: number; bond: number; cash: number },
): YearReturns[] {
  const k = recenterTo
    ? {
        equity: (1 + recenterTo.equity) / (1 + HISTORICAL_MEANS.equity),
        bond: (1 + recenterTo.bond) / (1 + HISTORICAL_MEANS.bond),
        cash: (1 + recenterTo.cash) / (1 + HISTORICAL_MEANS.cash),
      }
    : { equity: 1, bond: 1, cash: 1 };

  const seq: YearReturns[] = [];
  while (seq.length < years) {
    const start = Math.floor(rng() * HISTORICAL_YEARS);
    for (let i = 0; i < blockLength && seq.length < years; i++) {
      const idx = (start + i) % HISTORICAL_YEARS; // wrap at the series end
      // Without a target, hand back the historical values untouched — scaling
      // by 1.0 would introduce floating-point drift for no reason.
      seq.push(
        recenterTo
          ? {
              equity: (1 + EQUITY_RETURNS[idx]!) * k.equity - 1,
              bond: (1 + BOND_RETURNS[idx]!) * k.bond - 1,
              cash: (1 + CASH_RETURNS[idx]!) * k.cash - 1,
            }
          : { equity: EQUITY_RETURNS[idx]!, bond: BOND_RETURNS[idx]!, cash: CASH_RETURNS[idx]! },
      );
    }
  }
  return seq;
}

/**
 * Build one trial's sequence from lognormal draws with correlation.
 *
 * `means` are COMPOUND returns, matching how the Assumptions tab is worded and
 * how the deterministic projection grows balances. Setting mu = ln(1+target)
 * makes the median trial compound at exactly that rate, so the simulation and
 * the straight-line plan describe the same world; the arithmetic average of the
 * draws lands slightly higher, which is the correct relationship rather than an
 * error.
 */
export function parametricSequence(
  years: number,
  means: { equity: number; bond: number; cash: number },
  rng: () => number,
  equitySd = 0.17,
  bondSd = 0.06,
  corr = 0.1,
): YearReturns[] {
  const seq: YearReturns[] = [];
  const muE = Math.log(1 + means.equity);
  const muB = Math.log(1 + means.bond);
  for (let y = 0; y < years; y++) {
    const z1 = gaussian(rng);
    const z2raw = gaussian(rng);
    const z2 = corr * z1 + Math.sqrt(1 - corr * corr) * z2raw;
    seq.push({
      equity: Math.exp(muE + equitySd * z1) - 1,
      bond: Math.exp(muB + bondSd * z2) - 1,
      cash: means.cash, // cash vol is noise at this scale
    });
  }
  return seq;
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.round((p / 100) * (sorted.length - 1))));
  return sorted[idx]!;
}

export function runMonteCarlo(plan: Plan, options: MonteCarloOptions = {}): MonteCarloResult {
  const trials = options.trials ?? 1_000;
  const model = options.model ?? 'bootstrap';
  const blockLength = options.blockLength ?? 8;
  const flexCut = options.flexibleSpendingCut ?? 0;
  const rng = mulberry32(options.seed ?? 20260803);
  const a = activeAssumptions(plan);
  const recenter = options.recenter ?? true;

  // Horizon length from a base run (also gives us the year axis). This same
  // run is the straight-line plan shown at the top of the screen — kept here so
  // the card can place it inside the distribution instead of leaving the two
  // views to contradict each other.
  const base = runProjection(plan, CURRENT_YEAR);
  const years = base.snapshots.length;
  const baseLast = base.snapshots[years - 1]!;
  const deterministicTerminal = Math.max(
    0, baseLast.netWorth / baseLast.cumulativeInflationFactor,
  );

  const netWorthPaths: number[][] = Array.from({ length: years }, () => []);
  const failureYears: number[] = [];
  const terminalWealths: number[] = [];
  // Per failed trial: the average yearly spending gap, and the age it began.
  const shortfallsPerYear: number[] = [];
  const shortfallAges: number[] = [];
  const firstPersonId = plan.household.people[0]?.id;
  let successes = 0;
  let totalCutYears = 0;
  let worstCutYears = 0;

  for (let t = 0; t < trials; t++) {
    const seq =
      model === 'bootstrap'
        ? bootstrapSequence(
            years, blockLength, rng,
            recenter ? a.returnsByAssetClass : undefined,
          )
        : parametricSequence(
            years, a.returnsByAssetClass, rng,
            options.equitySd, options.bondSd, options.equityBondCorrelation,
          );

    const r = runProjection(plan, CURRENT_YEAR, {
      returnsByYear: seq,
      discretionaryCutFraction: flexCut > 0 ? flexCut : undefined,
    });

    if (r.firstDepletionYear === undefined) successes += 1;
    else {
      failureYears.push(r.firstDepletionYear);
      // How bad is "failure" here? Average the yearly gap over the short years,
      // in today's dollars, so it reads as a lifestyle cut rather than a verdict.
      const short = r.snapshots.filter((s) => s.unfundedSpending > 0);
      if (short.length > 0) {
        const gap = short.reduce(
          (t, s) => t + s.unfundedSpending / s.cumulativeInflationFactor, 0,
        ) / short.length;
        shortfallsPerYear.push(gap);
        const age = firstPersonId ? short[0]!.ages[firstPersonId] : undefined;
        if (age != null) shortfallAges.push(age);
      }
    }

    for (let y = 0; y < years; y++) {
      const s = r.snapshots[y]!;
      netWorthPaths[y]!.push(s.netWorth / s.cumulativeInflationFactor);
    }
    const last = r.snapshots[years - 1]!;
    terminalWealths.push(Math.max(0, last.netWorth / last.cumulativeInflationFactor));

    totalCutYears += r.flexCutYears;
    worstCutYears = Math.max(worstCutYears, r.flexCutYears);

    if (options.onProgress && (t + 1) % 25 === 0) options.onProgress(t + 1, trials);
  }

  const bands = base.snapshots.map((s, y) => {
    const sorted = [...netWorthPaths[y]!].sort((x, z) => x - z);
    return {
      year: s.year,
      p10: percentile(sorted, 10),
      p25: percentile(sorted, 25),
      p50: percentile(sorted, 50),
      p75: percentile(sorted, 75),
      p90: percentile(sorted, 90),
    };
  });

  // Percentile rank counting ties at half weight. Matters at the floor: when
  // the straight-line plan and most trials all end at $0, the honest answer is
  // "right in the middle, they agree", not "0th percentile".
  const below = terminalWealths.filter((w) => w < deterministicTerminal).length;
  const equal = terminalWealths.filter((w) => w === deterministicTerminal).length;
  const rank = below + equal / 2;
  const median = (xs: number[]): number | null => {
    if (xs.length === 0) return null;
    const s = [...xs].sort((x, z) => x - z);
    return s[Math.floor(s.length / 2)]!;
  };

  return {
    trials,
    model,
    inflationFactors: base.snapshots.map((s) => s.cumulativeInflationFactor),
    deterministicTerminal,
    deterministicPercentile: trials > 0 ? Math.round((rank / trials) * 100) : 0,
    medianShortfallPerYear: median(shortfallsPerYear) ?? 0,
    medianShortfallAge: median(shortfallAges),
    recentered: model === 'bootstrap' && recenter,
    equityMeanUsed:
      model === 'bootstrap' && !recenter
        ? HISTORICAL_MEANS.equity
        : a.returnsByAssetClass.equity,
    successRate: successes / trials,
    bands,
    failureYears: failureYears.sort((x, z) => x - z),
    terminalWealths: terminalWealths.sort((x, z) => x - z),
    avgCutYears: totalCutYears / trials,
    worstCutYears,
  };
}

/**
 * Deterministic sequence-of-returns stress test (spec §14): a severe
 * multi-year drawdown imposed in the first years of retirement — the specific
 * risk that destroys early retirements, and more legible than a distribution.
 */
export function stressTestSequence(plan: Plan): YearReturns[] {
  const a = activeAssumptions(plan);
  const base = runProjection(plan, CURRENT_YEAR);
  const years = base.snapshots.length;
  const firstRetYear = Math.min(
    ...plan.household.people.map((p) => p.birthYear + p.retirementAge),
  );
  const crashIdx = Math.max(0, firstRetYear - CURRENT_YEAR);
  const crash = [-0.25, -0.15, -0.1];

  return Array.from({ length: years }, (_, i) => {
    const offset = i - crashIdx;
    if (offset >= 0 && offset < crash.length) {
      return { equity: crash[offset]!, bond: 0.01, cash: a.returnsByAssetClass.cash };
    }
    return { ...a.returnsByAssetClass };
  });
}
